import React, { useState, useEffect } from "react";
import "../../../css/ReleaseAppointmentsModal.css";
import api from "../../../api/axiosConfig";
import { showToast } from '../../Shared/Toaster';


// Interfaces
interface Center {
    _id: string;
    name: string;
    city: string;
}

interface SlotTemplate {
    _id: string;
    name: string;
}

interface SlotEntry {
    id: number;
    date: string;
    templateId: string;
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    center: Center;
}

const CampReleaseAppointmentsModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, center }) => {
    const [templates, setTemplates] = useState<SlotTemplate[]>([]);
    const [loading, setLoading] = useState(false);

    const [appointmentsPerSlot, setAppointmentsPerSlot] = useState("");
    const [slots, setSlots] = useState<SlotEntry[]>([
        { id: Date.now(), date: "", templateId: "" }
    ]);

    const today = new Date().toISOString().split('T')[0];

    useEffect(() => {
        if (isOpen) {
            setAppointmentsPerSlot("");
            setSlots([{ id: Date.now(), date: "", templateId: "" }]);

            const fetchTemplates = async () => {
                try {
                    const token = sessionStorage.getItem("accessToken");
                    const res = await api.appointment_api.get("/api/slot-templates", {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    const activeTemplates = (res.data || []).filter(
                        (t: any) => !t.disabled_on && !t.disabled
                    );
                    setTemplates(activeTemplates);
                } catch (error) {
                    console.error("Failed to fetch slot templates:", error);
                }
            };
            fetchTemplates();
        }
    }, [isOpen]);

    const handleAddSlot = () => {
        setSlots([...slots, { id: Date.now(), date: "", templateId: "" }]);
    };

    const handleDeleteSlot = (id: number) => {
        if (slots.length > 1) {
            setSlots(slots.filter(slot => slot.id !== id));
        }
    };

    const handleSlotChange = (id: number, field: 'date' | 'templateId', value: string) => {
        setSlots(slots.map(slot => (slot.id === id ? { ...slot, [field]: value } : slot)));
    };

    const handleAppointmentsPerSlotChange = (value: string) => {
        if (value === "" || value === "3" || value === "5") {
            setAppointmentsPerSlot(value);
            return;
        }

        showToast("Appointments per slot can only be 3 or 5.", 'error');
    };

    const handleSubmit = async () => {
        if (!appointmentsPerSlot) {
            showToast("Please enter the number of appointments per slot.", 'error');
            return;
        }

        if (appointmentsPerSlot !== "3" && appointmentsPerSlot !== "5") {
            showToast("Appointments per slot can only be 3 or 5.", 'error');
            return;
        }

        if (slots.some(slot => !slot.date || !slot.templateId)) {
            showToast("Please ensure every slot has a date and a template selected.", 'error');
            return;
        }

        // ✅ **PAYLOAD ADJUSTMENT**
        // This payload now perfectly matches the structure your `createCampReleasedSlots` controller expects.
        const payload = {
            center_id: center._id,
            total_appt_per_slot: parseInt(appointmentsPerSlot, 10),
            // The key is now "dates" to match the backend
            dates: slots.map(slot => ({
                date: slot.date,
                slot_template_id: slot.templateId,
            })),
        };

        setLoading(true);
        try {
            const token = sessionStorage.getItem("accessToken");
            // ✅ **API ENDPOINT**
            // Sending the request to a new endpoint. Ensure you have a route like
            // `router.post('/released-slots/camp', createCampReleasedSlots);` in your backend router.
            await api.appointment_api.post("/api/released-slots/camp", payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            showToast("Camp appointments released successfully!", 'success');
            onSuccess?.();
            onClose();
        } catch (err: any) {
            console.error("Failed to release camp appointments:", err);
            // The detailed validation error from your backend will now be shown
            showToast(err.response?.data?.message || "An error occurred. Please try again.", 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content release-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Create Appointments</h2>
                    <button className="global-close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="release-form-section">
                    <h3>Center Information</h3>
                    <div className="info-grid">
                        <div><label>Center Name</label><p>{center.name}</p></div>
                        <div><label>City</label><p>{center.city}</p></div>
                    </div>
                </div>

                <div className="release-form-section">
                    <h3>Final Release Details</h3>
                    <div className="final-release">
                        <label htmlFor="appointments">Appointments per slot *</label>
                        <select
                            id="appointments"
                            value={appointmentsPerSlot}
                            onChange={(e) => handleAppointmentsPerSlotChange(e.target.value)}
                        >
                            <option value="">Select</option>
                            <option value="3">3</option>
                            <option value="5">5</option>
                        </select>
                    </div>
                </div>

                <div className="release-form-section">
                    <h3>Select Slots</h3>
                    <div className="slots-table">
                        <div className="slots-table-header">
                            <div className="header-cell">Slot Date</div>
                            <div className="header-cell">Slot Template</div>
                            <div className="header-cell">Action</div>
                        </div>

                        <div className="slots-table-body">
                            {slots.map((slot) => (
                                <div className="slot-row" key={slot.id}>
                                    <div className="slot-cell">
                                        <input
                                            type="date"
                                            value={slot.date}
                                            min={today}
                                            onChange={(e) => handleSlotChange(slot.id, 'date', e.target.value)}
                                        />
                                    </div>
                                    <div className="slot-cell">
                                        <select
                                            value={slot.templateId}
                                            onChange={(e) => handleSlotChange(slot.id, 'templateId', e.target.value)}
                                        >
                                            <option value="" disabled>Select a template</option>
                                            {templates.map(t => (
                                                <option key={t._id} value={t._id}>{t.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="slot-cell action-cell">
                                        <button className="delete-btn" onClick={() => handleDeleteSlot(slot.id)} disabled={slots.length === 1}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#d32f2f" viewBox="0 0 24 24">
                                                <path d="M3 6h18v2H3V6zm2 3h14l-1.5 12.5a2 2 0 0 1-2 1.5H8.5a2 2 0 0 1-2-1.5L5 9zm5 2v9h2v-9H10zm4 0v9h2v-9h-2zM9 4V2h6v2h5v2H4V4h5z" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <button className="add-slot-btn" onClick={handleAddSlot}>+ Add Day</button>
                </div>

                <div className="modal-actions">
                    <button className="btn-secondary" onClick={onClose} disabled={loading}>Close</button>
                    <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
                        {loading ? "Creating..." : "Create Appointment"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CampReleaseAppointmentsModal;
